import { Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import * as am5index from '@amcharts/amcharts5/index';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5exporting from '@amcharts/amcharts5/plugins/exporting';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
	selector: 'app-rotated-labels-column-chart',
	standalone: true,
	imports: [CommonModule, MatButtonModule, TranslateModule, MatIconModule],

	template: `<div [id]="chartId" style="width: 100%; height: 300px;"></div>
		<div class="d-flex justify-content-end align-items-center mb-4">
			<button class="filters-show-button " type="button" mat-raised-button color="primary" aria-label="Export chart button" (click)="exportAsImage()">
      <mat-icon class="m-0">download</mat-icon>
			</button>
		</div>`,
})
export class RotatedLabelsColumnChartComponent implements OnChanges, OnDestroy {
  chartId = `rotatedChartDiv-${Math.random().toString(36).substr(2, 9)}`;

	@Input() data: { [key: string]: any }[] = [];
	@Input() categoryField: string = 'country';
	@Input() valueField: string = 'value';

	private root!: am5index.Root;
	private exporting!: am5exporting.Exporting;

	constructor(private zone: NgZone) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (this.data?.length > 0 && this.categoryField && this.valueField) {
			this.renderChart();
		}
	}

	renderChart(): void {
		this.disposeChart();

		this.zone.runOutsideAngular(() => {
      const root = am5index.Root.new(this.chartId);
			this.root = root;

			root.setThemes([am5themes_Animated.new(root)]);

			const chart = root.container.children.push(
				am5xy.XYChart.new(root, {
					panX: true,
					panY: true,
					wheelX: 'panX',
					wheelY: 'zoomX',
					pinchZoomX: true,
					paddingLeft: 0,
					paddingRight: 1,
				})
			);

			const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
			cursor.lineY.set('visible', false);

			const xRenderer = am5xy.AxisRendererX.new(root, {
				minGridDistance: 30,
				minorGridEnabled: true,
			});

			xRenderer.labels.template.setAll({
				rotation: -45,
				centerY: am5index.p50,
				centerX: am5index.p100,
				paddingRight: 15,
			});

			const xAxis = chart.xAxes.push(
				am5xy.CategoryAxis.new(root, {
					categoryField: this.categoryField,
					renderer: xRenderer,
					tooltip: am5index.Tooltip.new(root, {}),
				})
			);

			const yAxis = chart.yAxes.push(
				am5xy.ValueAxis.new(root, {
					renderer: am5xy.AxisRendererY.new(root, {
						strokeOpacity: 0.1,
					}),
				})
			);

			const series = chart.series.push(
				am5xy.ColumnSeries.new(root, {
					name: 'Series 1',
					xAxis,
					yAxis,
					valueYField: this.valueField,
					categoryXField: this.categoryField,
					sequencedInterpolation: true,
          tooltip: am5index.Tooltip.new(root, {
            labelText: `{${this.valueField}}`,
            getFillFromSprite: false,
          }),
				})
			);

			series.columns.template.setAll({ cornerRadiusTL: 5, cornerRadiusTR: 5, strokeOpacity: 0 });

			series.columns.template.adapters.add('fill', (fill, target) => {
				return chart.get('colors')?.getIndex(series.columns.indexOf(target));
			});

			series.columns.template.adapters.add('stroke', (stroke, target) => {
				return chart.get('colors')?.getIndex(series.columns.indexOf(target));
			});

			xAxis.data.setAll(this.data);
			series.data.setAll(this.data);

			series.appear(1000);
			chart.appear(1000, 100);

			this.exporting = am5exporting.Exporting.new(root, {
				pngOptions: {
					quality: 0.9,
					maintainPixelRatio: true,
				},
			});
		});
	}

	exportAsImage() {
		this.exporting.download('png');
	}

	ngOnDestroy(): void {
		this.disposeChart();
	}

	private disposeChart() {
		if (this.root) {
			this.root.dispose();
		}
	}
}
